import React, { FC, ReactNode } from 'react'
import classNames from 'classnames'
import Tooltip from 'components/base/Tooltip'
import Input, { InputType } from './Input'
import Icon from 'project/Icon' // Ensure you import the correct path for Input

export interface InputGroupProps extends InputType {
  action?: ReactNode
  className?: string
  component?: ReactNode
  defaultValue?: string
  error?: string
  errorMessage?: string
  id?: string
  inputProps?: InputType
  isValid?: boolean
  onBlur?: InputType['onBlur']
  placeholder?: string
  required?: boolean
  textarea?: boolean
  title?: ReactNode
  tooltip?: string
  touched?: boolean
}

const InputGroup: FC<InputGroupProps> = ({
  action,
  className,
  component,
  defaultValue,
  disabled,
  error,
  errorMessage,
  id: providedId,
  inputProps = {},
  inputRef,
  isValid,
  name,
  onBlur,
  onChange,
  placeholder,
  required,
  textarea,
  title,
  tooltip,
  touched,
  type = 'text',
  value,
}) => {
  const id = providedId || name || inputProps.id || inputProps.name

  // Compute conditional isValid and errorMessage based on touched and error
  const computedIsValid =
    isValid !== undefined ? isValid : touched && error ? false : undefined
  const computedErrorMessage =
    errorMessage !== undefined
      ? errorMessage
      : touched && error
      ? error
      : undefined

  return (
    <div className={classNames('form-group', className)}>
      {(title || action) && (
        <div className='flex-row'>
          <Tooltip place='top' content={tooltip}>
            <label
              className='form-label d-flex align-items-center fw-semibold flex-fill'
              htmlFor={id}
            >
              {title}
              {tooltip && (
                <Icon
                  width={18}
                  fill='dark300'
                  name='info'
                  className='mx-2 text-dark300'
                />
              )}
              {required && <span className='text-danger'>*</span>}
            </label>
            {action}
          </Tooltip>
        </div>
      )}

      {component ? (
        component
      ) : (
        <Input
          name={name}
          touched={touched}
          textarea={textarea}
          inputRef={inputRef}
          onBlur={onBlur}
          {...inputProps}
          isValid={computedIsValid}
          disabled={disabled}
          value={value}
          data-test={['data-test']}
          defaultValue={defaultValue}
          onChange={onChange}
          type={type || 'text'}
          id={id}
          placeholder={placeholder}
        />
      )}
      {computedErrorMessage && (
        <span
          id={inputProps.name ? `${inputProps.name}-error` : ''}
          className='text-danger'
        >
          {computedErrorMessage}
        </span>
      )}
    </div>
  )
}

InputGroup.displayName = 'InputGroup'
export default InputGroup
